import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Bell, MessageCircle, Mail, Calendar, Users, DollarSign, Clock, CheckCircle2, AlertTriangle, Send } from 'lucide-react';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

export default function Reminders() {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ upcoming_count: 0, families_count: 0, total_amount: 0 });
  const [daysAhead, setDaysAhead] = useState('7');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentReminder, setCurrentReminder] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [generatedMessage, setGeneratedMessage] = useState(null);

  useEffect(() => {
    fetchReminders();
  }, [daysAhead]);

  const fetchReminders = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/reminders/upcoming-dues?days_ahead=${daysAhead}`);
      setReminders(response.data.reminders || []);
      setSummary({
        upcoming_count: response.data.upcoming_count,
        families_count: response.data.families_count,
        total_amount: response.data.total_amount
      });
    } catch (error) {
      toast.error('Erro ao carregar lembretes');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    const clean = phone.replace(/\D/g, '');
    if (clean.length === 11) {
      return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
    }
    return phone;
  };

  const getDaysUntilBadge = (days) => {
    if (days === 0) {
      return <span className="inline-flex px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">Hoje</span>;
    } else if (days === 1) {
      return <span className="inline-flex px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">Amanhã</span>;
    } else if (days <= 3) {
      return <span className="inline-flex px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">{days} dias</span>;
    } else {
      return <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">{days} dias</span>;
    }
  };

  const handleOpenReminder = async (reminder) => {
    setCurrentReminder(reminder);
    setCustomMessage('');
    setGeneratedMessage(null);
    setDialogOpen(true);

    try {
      const response = await api.post(`/reminders/generate-message?responsible_id=${reminder.responsible_id}`);
      setGeneratedMessage(response.data);
      setCustomMessage(response.data.message);
    } catch (error) {
      toast.error('Erro ao gerar mensagem');
    }
  };

  const handleSendWhatsApp = () => {
    if (generatedMessage?.whatsapp) {
      const phone = generatedMessage.whatsapp.replace(/\D/g, '');
      const fullPhone = phone.length <= 11 ? `55${phone}` : phone;
      const link = `https://wa.me/${fullPhone}?text=${encodeURIComponent(customMessage)}`;
      window.open(link, '_blank');
      logContact(currentReminder.responsible_id, 'whatsapp', 'Lembrete de vencimento');
      toast.success('WhatsApp aberto!');
    }
  };

  const handleSendEmail = () => {
    if (generatedMessage?.email) {
      const subject = encodeURIComponent('Lembrete de Mensalidade - DNACore');
      const body = encodeURIComponent(customMessage);
      window.open(`mailto:${generatedMessage.email}?subject=${subject}&body=${body}`, '_blank');
      logContact(currentReminder.responsible_id, 'email', 'Lembrete de vencimento');
      toast.success('Email aberto!');
    }
  };

  const logContact = async (responsibleId, contactType, notes) => {
    try {
      await api.post(`/communication/log-contact?responsible_id=${responsibleId}&contact_type=${contactType}&notes=${encodeURIComponent(notes)}`);
    } catch (error) {
      console.error('Erro ao registrar contato:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="reminders-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight flex items-center gap-3">
          <Bell className="w-9 h-9 text-primary" />
          Lembretes
        </h1>
        <p className="text-muted-foreground mt-1">Títulos próximos do vencimento para envio de lembretes</p>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Títulos a Vencer</p>
                <p className="text-2xl font-bold">{summary.upcoming_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Famílias</p>
                <p className="text-2xl font-bold">{summary.families_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Valor Total</p>
                <p className="text-2xl font-bold">{formatCurrency(summary.total_amount)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Período</Label>
              <Select value={daysAhead} onValueChange={setDaysAhead}>
                <SelectTrigger data-testid="days-ahead-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">Próximos 3 dias</SelectItem>
                  <SelectItem value="5">Próximos 5 dias</SelectItem>
                  <SelectItem value="7">Próximos 7 dias</SelectItem>
                  <SelectItem value="15">Próximos 15 dias</SelectItem>
                  <SelectItem value="30">Próximos 30 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Lembretes */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Famílias para Lembrete</CardTitle>
          <CardDescription>
            {reminders.length} famílias com títulos vencendo nos próximos {daysAhead} dias
          </CardDescription>
        </CardHeader>
        <CardContent>
          {reminders.length === 0 ? (
            <Alert className="border-green-500 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Nenhum título vence nos próximos {daysAhead} dias. Tudo em ordem!
              </AlertDescription>
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Responsável</TableHead>
                    <TableHead>Contato</TableHead>
                    <TableHead className="text-center">Títulos</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead className="text-center">Vencimento</TableHead>
                    <TableHead className="w-32">Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reminders.map((reminder) => (
                    <TableRow key={reminder.responsible_id} data-testid={`reminder-row-${reminder.responsible_id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{reminder.responsible_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {reminder.titles.map(t => t.student_name).join(', ')}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          {reminder.whatsapp && (
                            <div className="flex items-center gap-1 text-green-600">
                              <MessageCircle className="w-3 h-3" />
                              {formatPhone(reminder.whatsapp)}
                            </div>
                          )}
                          {reminder.email && (
                            <div className="flex items-center gap-1 text-blue-600">
                              <Mail className="w-3 h-3" />
                              <span className="truncate max-w-32">{reminder.email}</span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                          {reminder.titles.length}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {formatCurrency(reminder.total_amount)}
                      </TableCell>
                      <TableCell className="text-center">
                        {getDaysUntilBadge(reminder.min_days_until)}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          onClick={() => handleOpenReminder(reminder)}
                          data-testid={`send-reminder-${reminder.responsible_id}`}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Lembrar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Lembrete */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              Enviar Lembrete
            </DialogTitle>
            <DialogDescription>
              Para: {currentReminder?.responsible_name}
            </DialogDescription>
          </DialogHeader>

          {generatedMessage ? (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg text-sm space-y-1">
                <p><strong>Títulos:</strong> {generatedMessage.titles_count}</p>
                <p><strong>Valor Total:</strong> {formatCurrency(generatedMessage.total_amount)}</p>
                {currentReminder?.titles && (
                  <div className="mt-2 pt-2 border-t">
                    {currentReminder.titles.map((t, idx) => (
                      <p key={idx} className="text-xs">
                        • {t.student_name} - {formatDate(t.due_date)} - {formatCurrency(t.amount)}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Mensagem:</Label>
                <Textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  rows={10}
                  className="font-mono text-sm"
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Fechar
            </Button>
            <Button
              variant="outline"
              onClick={handleSendEmail}
              disabled={!generatedMessage?.email}
              className="text-blue-600"
            >
              <Mail className="w-4 h-4 mr-2" />
              Email
            </Button>
            <Button
              onClick={handleSendWhatsApp}
              disabled={!generatedMessage?.whatsapp}
              className="bg-green-600 hover:bg-green-700"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
